/**
 * Underline tab strip for the Live module (Übersicht / Mitarbeiter / Kommunikation).
 * Step ids stay stable (2/3/4); visual order comes from the `steps` prop.
 * Step 4 (Kommunikation) is Grantonomy-only, so `steps` is a prop rather than hardcoded.
 * Keyboard arrows / Home / End follow the visual order of `steps`, not numeric step id.
 *
 * No overflow-x-auto: a scrollable tablist hid the active underline and showed a right-edge
 * scroll affordance. Three short labels always fit the Live content column without scrolling.
 */

import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { tabUnderlineInactiveClass } from "@/components/ui/tabs";

export type LiveStep = 2 | 3 | 4;

export function LiveStepNav({
  steps,
  active,
  onSelect,
}: {
  steps: LiveStep[];
  active: LiveStep;
  onSelect: (step: LiveStep) => void;
}) {
  const { t } = useTranslation();
  const refs = useRef<Partial<Record<LiveStep, HTMLButtonElement | null>>>({});

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: LiveStep | undefined;
    if (event.key === "ArrowRight") next = steps[(index + 1) % steps.length];
    else if (event.key === "ArrowLeft") next = steps[(index - 1 + steps.length) % steps.length];
    else if (event.key === "Home") next = steps[0];
    else if (event.key === "End") next = steps[steps.length - 1];
    if (!next) return;
    event.preventDefault();
    onSelect(next);
    refs.current[next]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={t("projectLive.stepsLabel")}
      className="mt-8 flex gap-1 border-b border-border"
      data-testid="live-step-nav"
    >
      {steps.map((step, index) => {
        const isActive = step === active;
        return (
          <button
            key={step}
            type="button"
            role="tab"
            id={`live-tab-${step}`}
            aria-selected={isActive}
            aria-controls={`live-panel-${step}`}
            tabIndex={isActive ? 0 : -1}
            ref={(node) => {
              refs.current[step] = node;
            }}
            onClick={() => onSelect(step)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={`live-step-${step}`}
            className={`-mb-px whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              isActive
                ? "border-foreground text-foreground"
                : `border-transparent text-muted-foreground hover:border-border ${tabUnderlineInactiveClass}`
            }`}
          >
            {t(`projectLive.step${step}Title`)}
          </button>
        );
      })}
    </div>
  );
}
